/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { Button, Input, Modal, Select } from '../..';
import useValidator from '../../../hooks/useValidator';

export type PayOption = {
  amount: number;
  customer: string;
  provider: string;
  identityNo: string;
  cardBrand?: string;
  cardLastFour?: string;
  cardExpiry?: string;
};

type ModalProps = {
  onCloseModal: () => void;
  onAddPayOption: (option: PayOption) => void;
  payType: 'card' | 'mobile-money' | 'cash';
};

function PaymentOption({ onCloseModal, onAddPayOption, payType }: ModalProps) {
  const [payAmount, setPayAmount] = React.useState<number>();
  const [details, setDetails] = React.useState({
    customer: '',
    provider: '',
    identityNo: '',
    cardBrand: '',
    cardLastFour: '',
    cardExpiry: '',
  });
  const [errors, setErrors] = React.useState({
    provider: false,
  });
  const [validator, showValidationMessage] = useValidator({});

  const providerList =
    payType === 'card'
      ? [
          'Visa',
          'Mastercard',
          'American Express',
          'Discover',
          'Maestro',
          'UnionPay',
        ]
      : ['MTN', 'Vodafone', 'AirtelTigo'];

  const handleDetailsInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    setDetails(fndDetail => ({
      ...fndDetail,
      [name]: value,
    }));
  };

  const handlePayDetails = async () => {
    if (!validator.allValid()) {
      setErrors(errs => ({
        ...errs,
        provider: !validator.fieldValid('provider'),
      }));
      showValidationMessage(true);
      return;
    }

    let payOption = {
      amount: payAmount,
    } as PayOption;

    if (payType === 'card') {
      payOption = {
        ...payOption,
        customer: details.customer,
        provider: details.provider,
        identityNo: `${details.provider} - (${
          details.cardLastFour || '0000'
        } | ${details.cardExpiry || '00/00'})`, //  Visa - (4444 | 11/12)
        cardBrand: details.provider,
        cardLastFour: details.cardLastFour,
        cardExpiry: details.cardExpiry,
      };
    }

    if (payType === 'mobile-money') {
      payOption = {
        ...payOption,
        customer: details.customer,
        provider: details.provider,
        identityNo: details.identityNo,
      };
    }

    onAddPayOption(payOption);
    onCloseModal();
  };

  return (
    <Modal onClose={onCloseModal} show>
      <div className="rounded-md bg-white tablet:w-[500px]">
        <form>
          <div className="flex flex-col p-5">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre"
            >
              {payType === 'card'
                ? 'Credit Card Payment'
                : payType === 'cash'
                ? 'Cash Payment'
                : 'Mobile Money Payment'}
            </h3>
            <div className="text-pos-black text-sm mt-2 mb-2 font-normal space-y-4">
              <div>
                <label htmlFor="payAmount" className="text-xbase block mb-1">
                  Amount to Pay
                </label>
                <Input
                  name="payAmount"
                  placeholder="0.00"
                  value={payAmount !== undefined ? payAmount : ''}
                  type="number"
                  min={0}
                  onChange={event => setPayAmount(event.target.valueAsNumber)}
                />
              </div>

              {payType !== 'cash' && (
                <>
                  <div>
                    <label htmlFor="customer" className="text-xbase block mb-1">
                      Account Name
                    </label>
                    <Input
                      name="customer"
                      id="customer"
                      value={details.customer}
                      onChange={handleDetailsInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="provider" className="text-xbase block mb-1">
                      {payType === 'card' ? 'Card Provider ' : 'Provider '}
                      <span className="text-xs font-light text-pos-red">*</span>
                    </label>
                    <Select
                      placeholder={{
                        value: '',
                        label: 'Select Account Provider',
                      }}
                      value={details.provider}
                      options={[...providerList, 'Other'].map(value => ({
                        label: value,
                        value,
                      }))}
                      name="provider"
                      id="provider"
                      onChange={handleDetailsInputChange}
                      onBlur={() => {
                        setErrors(errs => ({
                          ...errs,
                          provider: !validator.check(
                            details.provider,
                            'required',
                          ),
                        }));
                      }}
                      error={errors.provider}
                      className="w-full"
                    />
                    {validator.message(
                      'provider',
                      details.provider,
                      'required',
                      {
                        messages: {
                          required: `Provider is required.`,
                        },
                        className:
                          'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                      },
                    )}
                  </div>
                  {payType === 'card' ? (
                    <div className="flex flex-row mb-1 gap-5">
                      <div className="w-full">
                        <label
                          htmlFor="cardLastFour"
                          className="text-xbase block"
                        >
                          Card Last Four No.
                        </label>
                        <Input
                          name="cardLastFour"
                          id="cardLastFour"
                          placeholder="0000"
                          value={details.cardLastFour}
                          onChange={event =>
                            setDetails(fndDetails => {
                              if (!event.target.value.match('^[0-9/]*$')) {
                                return fndDetails;
                              }
                              return {
                                ...fndDetails,
                                cardLastFour: event.target.value,
                              };
                            })
                          }
                          maxLength={4}
                          size={4}
                        />
                      </div>
                      <div className="w-full">
                        <label
                          htmlFor="cardExpiry"
                          className="text-xbase block"
                        >
                          Card Expiry
                        </label>
                        <Input
                          name="cardExpiry"
                          id="cardExpiry"
                          placeholder="MM/YY"
                          value={details.cardExpiry}
                          maxLength={5}
                          size={5}
                          onChange={event =>
                            setDetails(fndDetails => {
                              if (!event.target.value.match('^[0-9/]*$')) {
                                return fndDetails;
                              }
                              return {
                                ...fndDetails,
                                cardExpiry: event.target.value
                                  .replace(/^(\d\d)(\d)$/g, '$1/$2')
                                  .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2')
                                  .replace(/[^\d/]/g, ''),
                              };
                            })
                          }
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label
                        htmlFor="identityNo"
                        className="text-xbase block mb-1"
                      >
                        Account Number
                      </label>
                      <Input
                        name="identityNo"
                        id="identityNo"
                        type="tel"
                        value={details.identityNo}
                        onChange={handleDetailsInputChange}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="mt-8 text-center flex flex-row ml-auto space-x-4">
              <Button
                label="Continue"
                onClick={handlePayDetails}
                type="button"
                disabled={false}
                className="!bg-pos-green border border-[rgba(27,31,36,0.15)] min-w-[150px]  h-[40px]
                      items-center justify-center text-white text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default PaymentOption;
